import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import { getWaitingFor } from '../ComplaintsDashboard';
import { SUBTYPE_LABELS } from '../utils/complaintCategories';
import { handleImagePaste } from '../utils/pasteFiles';
import CloseComplaintEarly from './CloseComplaintEarly.jsx';

const API_BASE = process.env.REACT_APP_API_URL;

const SUPPLIER_LABELS = { gpv: 'GPV', ubiqconn: 'Ubiqconn', acmepoint: 'Acmepoint', hk_ringer: 'HK Ringer' };
const ESCALATION_COLORS = { 1: colors.success, 2: colors.statusYellow, 3: colors.danger };

const STATUS_LABELS = {
  draft: 'Draft', submitted: 'Submitted', acknowledged: 'Acknowledged',
  initial_report: 'Initial Report', fast_close_pending: 'Fast Close — Awaiting Payment',
  fast_close_confirmed: 'Fast Close — Payment Confirmed', '8d_report': '8D Report',
  corrective_action: 'Corrective Action', monitoring: 'Monitoring',
  closed: 'Closed', archived: 'Archived',
};

const COMP_STATUS_LABELS = {
  pending: 'Pending', challenged: 'Challenged',
  partially_accepted: 'Partially Accepted', accepted: 'Accepted', rejected: 'Rejected',
};

// Per-status: what NIN sees as their action prompt
const NIN_ACTION_PROMPTS = {
  draft: { label: 'Submit to supplier', hint: 'Review and submit this complaint to the supplier via the share link.' },
  initial_report: { label: 'Review initial report', hint: 'Supplier has submitted their initial investigation. You can challenge the fault assessment, request the full 8D process, or fast-close by requesting the money back directly.' },
  fast_close_confirmed: { label: 'Confirm payment received', hint: 'Supplier has confirmed payment was sent. Confirm receipt to close the complaint.' },
  corrective_action: { label: 'Review corrective action plan', hint: 'Supplier submitted their CA plan with proposed monitoring lots. Accept or propose different lot numbers.' },
  monitoring: { label: 'Verify lot results', hint: 'The supplier submits each lot from their portal. Verify them as they arrive, request corrective action on any failure, and close the complaint with a final report once the schedule is complete.' },
};
const NIN_DYNAMIC_PROMPTS = (complaint) => {
  const fc = complaint.faultChallenge;
  if (complaint.status === 'initial_report' && fc?.status === 'supplier_responded' && !fc?.supplierAcceptsResponsibility) {
    return { label: 'Resolve fault challenge', hint: 'Supplier maintains it is a customer fault. You can overrule them and proceed to 8D, or accept their position (no payment due).' };
  }
  if (complaint.status === 'fast_close_pending' && complaint.fastClose?.gpvDeclinedAt) {
    const amt = complaint.fastClose.gpvCounterAmount;
    return { label: 'GPV countered the fast-close', hint: amt ? `GPV proposes €${amt.toLocaleString()} instead. Accept their amount or send your own counter.` : 'GPV declined the fast-close request without a counter-offer. Send a new proposal or take a different path.' };
  }
  return NIN_ACTION_PROMPTS[complaint.status];
};
const SUPPLIER_ACTION_PROMPTS = {
  submitted: 'Waiting for supplier to acknowledge receipt of this complaint.',
  acknowledged: 'Waiting for supplier to submit their initial investigation report.',
  fast_close_pending: 'Fast-close requested. Waiting for supplier to confirm payment has been sent.',
  '8d_report': '8D report has been requested. Waiting for supplier to submit it.',
  corrective_action: 'Corrective action plan has been requested. Waiting for supplier to submit it.',
};

// Map status → which tab should be active
const DEFAULT_TAB = {
  draft: 'overview', submitted: 'overview', acknowledged: 'overview',
  initial_report: 'overview', fast_close_pending: 'overview', fast_close_confirmed: 'overview',
  '8d_report': '8d_report', corrective_action: 'corrective_action',
  monitoring: 'monitoring', closed: 'final_report', archived: 'final_report',
};

// Stage timings for display
function getStageTimes(c) {
  const m = c.metrics || {};
  return [
    { label: 'Time to acknowledge', value: m.timeToAcknowledgeHours != null ? `${m.timeToAcknowledgeHours}h` : null },
    { label: 'Time to initial report', value: m.timeToInitialReportDays != null ? `${m.timeToInitialReportDays}d` : null },
    { label: 'Time to 8D', value: m.timeTo8DDays != null ? `${m.timeTo8DDays}d` : null },
    { label: 'Time to CA plan', value: m.timeToCorrectiveActionDays != null ? `${m.timeToCorrectiveActionDays}d` : null },
    { label: 'Total time to close', value: m.timeToCloseDays != null ? `${m.timeToCloseDays}d` : null },
  ].filter(t => t.value !== null);
}

export default function ComplaintDetailModal({ complaint: initial, onClose, onUpdated }) {
  const { getToken } = useAuth();
  const [complaint, setComplaint] = useState(initial);
  const [tab, setTab] = useState(DEFAULT_TAB[initial.status] || 'overview');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const update = (updated) => { setComplaint(updated); onUpdated(updated); };

  const apiCall = async (path, method = 'POST', bodyObj = null, isFormData = false) => {
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const opts = { method, headers: { Authorization: `Bearer ${token}` } };
      if (bodyObj) {
        if (isFormData) { opts.body = bodyObj; }
        else { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(bodyObj); }
      }
      const res = await fetch(`${API_BASE}${path}`, opts);
      const data = await res.json();
      if (!data.ok) { setError(data.message || data.error || 'Failed.'); return null; }
      return data;
    } catch (e) { setError('Network error.'); return null; }
    finally { setSaving(false); }
  };

  const waitingFor = getWaitingFor(complaint);
  const isNinAction = waitingFor === 'nin';
  const ninPrompt = NIN_DYNAMIC_PROMPTS(complaint);
  const gpvPrompt = SUPPLIER_ACTION_PROMPTS[complaint.status];

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'quality', label: `Quality${complaint.qualityLinks?.length ? ` (${complaint.qualityLinks.length})` : ''}` },
    { key: '8d_report', label: '8D Report' },
    { key: 'corrective_action', label: 'Corrective Action' },
    { key: 'monitoring', label: 'Monitoring' },
    { key: 'compensation', label: 'Compensation' },
    { key: 'final_report', label: 'Final Report' },
    { key: 'history', label: 'History' },
  ];

  const shareUrl = `${window.location.origin}/supplier-complaint/${complaint.accessToken}`;

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        {/* Header */}
        <div style={modalHeader}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, fontSize: '1rem', color: colors.textStrong }}>{complaint.complaintId}</span>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.12rem 0.4rem', borderRadius: 4, background: colors.surfaceRaised, color: ESCALATION_COLORS[complaint.escalationLevel] }}>
                L{complaint.escalationLevel}
              </span>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.primary }}>
                {SUPPLIER_LABELS[complaint.supplier] || complaint.supplier}
              </span>
              <span style={{ fontSize: '0.72rem', color: colors.textMuted, background: colors.surfaceRaised, padding: '0.12rem 0.4rem', borderRadius: 4 }}>
                {STATUS_LABELS[complaint.status] || complaint.status}
              </span>
              {complaint.testInstance && (
                <span
                  title="Kept out of the retrieval corpus. Not yet excluded from the quality figures."
                  style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#fbbf24', background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)', padding: '0.1rem 0.4rem', borderRadius: 999 }}
                >
                  Test
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.88rem', color: colors.textBody, marginTop: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{complaint.title}</div>
          </div>
          {!['closed', 'archived'].includes(complaint.status) && (
            <div style={{ flexShrink: 0, marginRight: '0.4rem' }}>
              <CloseComplaintEarly complaint={complaint} onClosed={update} />
            </div>
          )}
          <button onClick={onClose} className="ni-button ni-button--ghost" style={{ padding: '0.25rem 0.6rem', flexShrink: 0 }}>✕</button>
        </div>

        {/* Action banner */}
        {isNinAction && ninPrompt && (
          <div style={{ padding: '0.65rem 1.25rem', background: `${colors.danger}18`, borderBottom: `1px solid ${colors.danger}40`, display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <span style={{ color: colors.danger, fontSize: '0.9rem', marginTop: '0.05rem' }}>⚡</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.8rem', color: colors.danger }}>NIN ACTION REQUIRED — {ninPrompt.label}</div>
              <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.1rem' }}>{ninPrompt.hint}</div>
            </div>
          </div>
        )}
        {!isNinAction && gpvPrompt && (
          <div style={{ padding: '0.65rem 1.25rem', background: `${colors.primary}12`, borderBottom: `1px solid ${colors.primary}30`, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ color: colors.primary, fontSize: '0.9rem' }}>⏳</span>
            <div style={{ fontSize: '0.78rem', color: colors.textMuted }}>{gpvPrompt}</div>
          </div>
        )}

        {/* Supplier share link */}
        {complaint.accessToken && (
          <div style={{ padding: '0.45rem 1.25rem', background: colors.surfaceMuted, borderBottom: `1px solid ${colors.panelBorder}`, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.68rem', color: colors.textDim, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>Supplier link:</span>
            <code style={{ fontSize: '0.72rem', color: colors.textMuted, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shareUrl}</code>
            <button type="button" className="ni-button ni-button--ghost" style={{ fontSize: '0.68rem', padding: '0.15rem 0.45rem' }} onClick={() => navigator.clipboard.writeText(shareUrl)}>
              Copy link
            </button>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${colors.panelBorder}`, overflowX: 'auto' }}>
          {tabs.map(t => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '0.6rem 0.9rem', fontSize: '0.75rem', fontWeight: 600,
              color: tab === t.key ? colors.primary : colors.textMuted,
              borderBottom: tab === t.key ? `2px solid ${colors.primary}` : '2px solid transparent',
              whiteSpace: 'nowrap',
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '1.25rem', overflowY: 'auto', flex: 1 }}>
          {error && <p style={{ color: colors.danger, fontSize: '0.82rem', marginBottom: '1rem', background: `${colors.danger}15`, padding: '0.5rem 0.75rem', borderRadius: 6 }}>{error}</p>}

          {tab === 'overview' && <OverviewTab complaint={complaint} apiCall={apiCall} onUpdated={update} saving={saving} onTabChange={setTab} />}
          {tab === 'quality' && <QualityTab complaint={complaint} apiCall={apiCall} onUpdated={update} />}
          {tab === '8d_report' && <EightDTab complaint={complaint} apiCall={apiCall} onUpdated={update} saving={saving} />}
          {tab === 'corrective_action' && <CorrectiveActionTab complaint={complaint} apiCall={apiCall} onUpdated={update} saving={saving} />}

          {tab === 'monitoring' && <MonitoringTab complaint={complaint} apiCall={apiCall} onUpdated={update} saving={saving} onTabChange={setTab} />}
          {tab === 'compensation' && <CompensationTab complaint={complaint} apiCall={apiCall} onUpdated={update} saving={saving} />}
          {tab === 'final_report' && <FinalReportTab complaint={complaint} apiCall={apiCall} onUpdated={update} saving={saving} />}
          {tab === 'history' && <HistoryTab complaint={complaint} />}
        </div>
      </div>
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────

function OverviewTab({ complaint, apiCall, onUpdated, saving, onTabChange }) {
  const [advanceNote, setAdvanceNote] = useState('');
  const [fastCloseNote, setFastCloseNote] = useState('');
  const [fastCloseAmount, setFastCloseAmount] = useState('');
  const [showFastClose, setShowFastClose] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showFastCloseCounter, setShowFastCloseCounter] = useState(false);
  const [counterAmount, setCounterAmount] = useState('');
  const [counterNote, setCounterNote] = useState('');
  const [faultChallengeNote, setFaultChallengeNote] = useState('');
  const [showFaultChallenge, setShowFaultChallenge] = useState(false);
  const waitingFor = getWaitingFor(complaint);
  const fc = complaint.faultChallenge || {};
  const fastClose = complaint.fastClose || {};

  const handleAdvance = async (toStatus) => {
    const d = await apiCall(`/complaints/${complaint._id}/advance`, 'POST', { note: advanceNote, toStatus });
    if (d) { onUpdated(d.complaint); setAdvanceNote(''); }
  };

  const handleFastClose = async () => {
    const d = await apiCall(`/complaints/${complaint._id}/fast-close`, 'POST', {
      note: fastCloseNote,
      compensationRequested: fastCloseAmount || undefined,
    });
    if (d) { onUpdated(d.complaint); setShowFastClose(false); }
  };

  const handleFastCloseCounter = async () => {
    const d = await apiCall(`/complaints/${complaint._id}/fast-close-counter`, 'POST', {
      counterAmount: counterAmount || undefined,
      note: counterNote,
    });
    if (d) { onUpdated(d.complaint); setShowFastCloseCounter(false); setCounterAmount(''); setCounterNote(''); setShowFastClose(false); }
  };

  const handleFastCloseReceived = async () => {
    const d = await apiCall(`/complaints/${complaint._id}/fast-close-received`, 'POST', {});
    if (d) onUpdated(d.complaint);
  };

  const handleFaultChallenge = async () => {
    const d = await apiCall(`/complaints/${complaint._id}/fault-challenge`, 'POST', { note: faultChallengeNote });
    if (d) { onUpdated(d.complaint); setShowFaultChallenge(false); setFaultChallengeNote(''); }
  };

  const handleFaultChallengeResolve = async (decision) => {
    const d = await apiCall(`/complaints/${complaint._id}/fault-challenge-resolve`, 'POST', { decision });
    if (d) onUpdated(d.complaint);
  };

  const stageTimes = getStageTimes(complaint);
  const isArchived = complaint.status === 'archived';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Grid>
        <InfoItem label="Supplier" value={SUPPLIER_LABELS[complaint.supplier] || complaint.supplier} />
        <InfoItem label="Status" value={STATUS_LABELS[complaint.status] || complaint.status} />
        <InfoItem label="Escalation" value={`Level ${complaint.escalationLevel}`} />
        {complaint.submittedAt && <InfoItem label="Submitted" value={new Date(complaint.submittedAt).toLocaleDateString('en-CA')} />}
        {complaint.productNumber && <InfoItem label="Product No." value={complaint.productNumber} />}
        {complaint.batchNumber && <InfoItem label="Batch No." value={complaint.batchNumber} />}
        {complaint.quantity != null && <InfoItem label="Quantity" value={complaint.quantity} />}
        {complaint.affectedValue != null && <InfoItem label="Affected Value" value={`€${Number(complaint.affectedValue).toLocaleString()}`} />}
      </Grid>

      {/* The units this non-conformance covers (issue #748). Recording them and
          never showing them would be worse than not recording them: the case
          would look traceable while nobody could see what it was traced to. */}
      {Array.isArray(complaint.units) && complaint.units.length > 0 && (
        <div>
          <Label>Affected units ({complaint.units.length})</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.35rem' }}>
            {complaint.units.map((u, i) => {
              const tag = u.serviceTagNumber || u.serialNumber || '—';
              return (
                <span
                  key={u._id || tag || i}
                  title={[u.productNumber, u.model].filter(Boolean).join(' · ') || 'No production record'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                    fontSize: '0.8rem', padding: '0.2rem 0.5rem', borderRadius: 8,
                    border: `1px solid ${u.verified ? 'rgba(70,187,137,0.35)' : 'rgba(251,191,36,0.35)'}`,
                    background: u.verified ? 'rgba(70,187,137,0.1)' : 'rgba(251,191,36,0.1)',
                    color: colors.textBody,
                  }}
                >
                  {u.deviceId ? (
                    <a href={`/device/${u.deviceId}`} target="_blank" rel="noopener noreferrer"
                       style={{ color: colors.primary, textDecoration: 'none' }}>{tag}</a>
                  ) : tag}
                  {/* An unverified unit must never be mistaken for a verified
                      one -- that is the whole reason the flag exists. */}
                  {!u.verified && (
                    <span style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.04em', color: '#fbbf24' }}>
                      UNVERIFIED
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* The supplier's investigation. NI is asked to choose between challenging,
          8D and fast-close on the strength of this — it was previously not shown
          anywhere on the NI side at all. */}
      {complaint.initialReport?.submittedAt && (
        <div style={{ background: colors.surfaceMuted, borderRadius: 8, padding: '0.75rem 0.85rem', border: `1px solid ${colors.panelBorder}` }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Label>Supplier Initial Report</Label>
            <span style={{ fontSize: '0.7rem', color: colors.textDim }}>
              {complaint.initialReport.submittedBy || 'Supplier'} · {new Date(complaint.initialReport.submittedAt).toLocaleDateString('en-CA')}
            </span>
          </div>
          {complaint.initialReport.content ? (
            <p style={{ color: colors.textBody, fontSize: '0.85rem', margin: '0.45rem 0 0', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
              {complaint.initialReport.content}
            </p>
          ) : (
            <p style={{ color: colors.textDim, fontSize: '0.82rem', margin: '0.45rem 0 0', fontStyle: 'italic' }}>
              No written findings were provided.
            </p>
          )}
          {complaint.initialReport.files?.length > 0 && (
            <div style={{ marginTop: '0.6rem' }}>
              <FileList files={complaint.initialReport.files} label="Supplier attachments" />
            </div>
          )}
        </div>
      )}

      {complaint.initialReport?.faultType && complaint.initialReport.faultType !== 'tbd' && (
        <div style={{ background: complaint.initialReport.faultType === 'workmanship' ? `${colors.danger}10` : `${colors.statusYellow}10`, borderRadius: 8, padding: '0.65rem 0.85rem', border: `1px solid ${complaint.initialReport.faultType === 'workmanship' ? colors.danger+'30' : colors.statusYellow+'30'}` }}>
          <Label>Supplier Fault Assessment</Label>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginTop: '0.3rem', color: complaint.initialReport.faultType === 'workmanship' ? colors.danger : colors.statusYellow }}>
            {complaint.initialReport.faultType === 'workmanship' ? 'Workmanship — Supplier accepts responsibility' : 'Customer Fault — Supplier attributes to customer'}
          </div>
          {/* NIN can challenge a customer-fault claim */}
          {complaint.status === 'initial_report' && complaint.initialReport.faultType === 'customer_fault' && !fc.status && !isArchived && (
            <button onClick={() => setShowFaultChallenge(v => !v)} className="ni-button ni-button--ghost" style={{ fontSize: '0.75rem', marginTop: '0.6rem' }}>
              Challenge this assessment
            </button>
          )}
          {showFaultChallenge && (
            <div style={{ marginTop: '0.65rem', background: colors.surfaceMuted, borderRadius: 8, padding: '0.75rem' }}>
              <Label>Explain why you dispute the customer-fault claim</Label>
              <textarea value={faultChallengeNote} onChange={e => setFaultChallengeNote(e.target.value)} className="ni-input" rows={3} placeholder="Reason for challenging the fault assessment..." style={{ resize: 'vertical', marginTop: '0.35rem', marginBottom: '0.5rem' }} />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleFaultChallenge} disabled={saving} className="ni-button ni-button--primary" style={{ fontSize: '0.8rem' }}>{saving ? 'Sending…' : 'Send Challenge to Supplier'}</button>
                <button onClick={() => setShowFaultChallenge(false)} className="ni-button ni-button--ghost" style={{ fontSize: '0.8rem' }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fault challenge status panels */}
      {fc.status === 'challenged' && (
        <div style={{ background: `${colors.statusYellow}10`, border: `1px solid ${colors.statusYellow}40`, borderRadius: 8, padding: '0.75rem' }}>
          <Label style={{ color: colors.statusYellow }}>Fault Challenge — Awaiting Supplier Response</Label>
          <p style={{ color: colors.textMuted, fontSize: '0.82rem', margin: '0.35rem 0 0' }}>{fc.challengeNote}</p>
        </div>
      )}
      {fc.status === 'supplier_responded' && !fc.supplierAcceptsResponsibility && (
        <div style={{ background: `${colors.danger}10`, border: `1px solid ${colors.danger}30`, borderRadius: 8, padding: '0.85rem' }}>
          <Label style={{ color: colors.danger }}>Supplier Maintains Customer Fault Position</Label>
          <p style={{ color: colors.textBody, fontSize: '0.85rem', margin: '0.35rem 0 0.75rem', whiteSpace: 'pre-wrap' }}>{fc.supplierResponseNote || 'No additional explanation provided.'}</p>
          <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
            <button onClick={() => handleFaultChallengeResolve('workmanship')} disabled={saving} className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }}>
              Overrule — proceed with 8D as workmanship
            </button>
            <button onClick={() => handleFaultChallengeResolve('customer_fault_confirmed')} disabled={saving} className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }}>
              Accept customer fault — close with no payment
            </button>
          </div>
        </div>
      )}
      {fc.status === 'resolved' && fc.resolvedDecision && (
        <div style={{ background: `${colors.success}10`, border: `1px solid ${colors.success}30`, borderRadius: 8, padding: '0.65rem 0.85rem', fontSize: '0.82rem', color: colors.success, fontWeight: 600 }}>
          ✓ Fault challenge resolved: {fc.resolvedDecision === 'workmanship' ? 'Treated as workmanship' : 'Customer fault confirmed'}
        </div>
      )}

      {complaint.complaintTypes?.length > 0 && (
        <div>
          <Label>Complaint Types</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.35rem' }}>
            {complaint.complaintTypes.map(t => (
              <span key={t} style={{ fontSize: '0.72rem', background: colors.surfaceRaised, color: colors.textMuted, padding: '0.18rem 0.5rem', borderRadius: 4 }}>
                {SUBTYPE_LABELS[t] || t.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {complaint.description && (
        <div>
          <Label>Description</Label>
          <p style={{ color: colors.textBody, fontSize: '0.88rem', margin: '0.35rem 0 0', whiteSpace: 'pre-wrap' }}>{complaint.description}</p>
        </div>
      )}

      {complaint.linkedRmaNumber && (
        <div style={{ background: `${colors.primary}10`, borderRadius: 8, padding: '0.75rem', border: `1px solid ${colors.primary}30` }}>
          <Label>Linked RMA Case</Label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.35rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '0.88rem', color: colors.primary }}>{complaint.linkedRmaNumber}</span>
            <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>Escalated from this RMA. The customer repair flow continues in parallel.</span>
          </div>
        </div>
      )}

      {complaint.internalNotes && (
        <div style={{ background: colors.surfaceMuted, borderRadius: 8, padding: '0.75rem' }}>
          <Label>Internal Notes (NIN only)</Label>
          <p style={{ color: colors.textMuted, fontSize: '0.82rem', margin: '0.35rem 0 0', whiteSpace: 'pre-wrap' }}>{complaint.internalNotes}</p>
        </div>
      )}

      <FileList files={complaint.evidence} label="NIN Evidence" />

      {stageTimes.length > 0 && (
        <div style={{ background: colors.surfaceMuted, borderRadius: 8, padding: '0.75rem' }}>
          <Label>Stage Timings</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '0.5rem' }}>
            {stageTimes.map(t => <InfoItem key={t.label} label={t.label} value={t.value} />)}
          </div>
        </div>
      )}

      {/* Open info requests from GPV */}
      {!isArchived && complaint.infoRequests?.filter(r => r.status === 'open').map(req => (
        <InfoRequestCard key={req.id} request={req} complaintId={complaint._id} apiCall={apiCall} onUpdated={onUpdated} saving={saving} />
      ))}

      {/* GPV declined fast-close — show counter and NIN response options (sub-state within fast_close_pending) */}
      {complaint.status === 'fast_close_pending' && fastClose.gpvDeclinedAt && (
        <div style={{ background: `${colors.danger}10`, border: `1px solid ${colors.danger}30`, borderRadius: 8, padding: '0.85rem' }}>
          <Label style={{ color: colors.danger }}>GPV Declined Fast-Close</Label>
          {fastClose.gpvDeclinedNote && <p style={{ color: colors.textBody, fontSize: '0.85rem', margin: '0.35rem 0 0.5rem', whiteSpace: 'pre-wrap' }}>{fastClose.gpvDeclinedNote}</p>}
          {fastClose.gpvCounterAmount != null && (
            <div style={{ marginBottom: '0.65rem' }}>
              <Label>GPV Counter-offer</Label>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: colors.statusYellow, marginTop: '0.2rem' }}>€{fastClose.gpvCounterAmount.toLocaleString()}</div>
            </div>
          )}
          {/* Negotiation history */}
          {fastClose.negotiations?.length > 0 && (
            <details style={{ marginBottom: '0.65rem' }}>
              <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: colors.textMuted }}>Negotiation history ({fastClose.negotiations.length} exchanges)</summary>
              <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {fastClose.negotiations.map((n, i) => (
                  <div key={i} style={{ fontSize: '0.78rem', color: colors.textMuted, display: 'flex', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 700, color: n.from === 'nin' ? colors.primary : colors.statusYellow }}>{n.from.toUpperCase()}</span>
                    {n.amount != null && <span>€{n.amount.toLocaleString()}</span>}
                    {n.note && <span style={{ color: colors.textDim }}>— {n.note}</span>}
                  </div>
                ))}
              </div>
            </details>
          )}
          {!showFastCloseCounter ? (
            <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
              {fastClose.gpvCounterAmount != null && (
                <button onClick={handleFastCloseCounter} disabled={saving} className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }}>
                  {saving ? 'Accepting…' : `Accept €${fastClose.gpvCounterAmount.toLocaleString()}`}
                </button>
              )}
              <button onClick={() => setShowFastCloseCounter(true)} className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }}>Send different counter</button>
            </div>
          ) : (
            <div style={{ background: colors.surfaceMuted, borderRadius: 8, padding: '0.75rem', marginTop: '0.5rem' }}>
              <Label>Your counter-offer</Label>
              <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', margin: '0.5rem 0' }}>
                <div style={{ flex: '1 1 140px' }}>
                  <Label>Amount (€)</Label>
                  <input type="number" value={counterAmount} onChange={e => setCounterAmount(e.target.value)} className="ni-input" placeholder={fastClose.gpvCounterAmount || ''} style={{ marginTop: '0.3rem' }} />
                </div>
                <div style={{ flex: '2 1 200px' }}>
                  <Label>Note</Label>
                  <input value={counterNote} onChange={e => setCounterNote(e.target.value)} className="ni-input" placeholder="Explain your position..." style={{ marginTop: '0.3rem' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleFastCloseCounter} disabled={saving} className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }}>{saving ? 'Sending…' : 'Send Counter'}</button>
                <button onClick={() => setShowFastCloseCounter(false)} className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Initial report actions: only when fault challenge is resolved/absent */}
      {complaint.status === 'initial_report' && !['challenged', 'supplier_responded'].includes(fc.status) && !showFastClose && (
        <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '1rem', display: 'flex', gap: '0.65rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={() => handleAdvance('8d_report')} disabled={saving} className="ni-button ni-button--primary">
            {saving ? 'Processing…' : 'Request 8D Report →'}
          </button>
          <button onClick={() => setShowFastClose(true)} className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }}>
            Fast-close — just get the money back
          </button>
        </div>
      )}
      {complaint.status === 'initial_report' && showFastClose && (
        <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '1rem', background: `${colors.statusYellow}0f`, borderRadius: 8, padding: '0.85rem' }}>
          <Label style={{ color: colors.statusYellow }}>Fast-Close — Skip 8D, request compensation directly</Label>
          <p style={{ color: colors.textMuted, fontSize: '0.8rem', margin: '0.4rem 0 0.75rem' }}>
            Fault is accepted. No 8D or monitoring required. Supplier will be asked to confirm payment and then the case is closed.
          </p>
          <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', marginBottom: '0.65rem' }}>
            <div style={{ flex: '1 1 140px' }}>
              <Label>Compensation to request (€)</Label>
              <input type="number" value={fastCloseAmount} onChange={e => setFastCloseAmount(e.target.value)}
                className="ni-input" placeholder={`${complaint.compensation?.totalClaimed || ''}`} style={{ marginTop: '0.3rem' }} />
            </div>
            <div style={{ flex: '2 1 200px' }}>
              <Label>Note to supplier</Label>
              <input value={fastCloseNote} onChange={e => setFastCloseNote(e.target.value)}
                className="ni-input" placeholder="We accept the initial report and request compensation..." style={{ marginTop: '0.3rem' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleFastClose} disabled={saving} className="ni-button ni-button--primary">{saving ? 'Sending…' : 'Send Fast-Close Request'}</button>
            <button onClick={() => setShowFastClose(false)} className="ni-button ni-button--ghost">Cancel</button>
          </div>
        </div>
      )}

      {/* Fast-close pending info — only when not in GPV-declined sub-state */}
      {complaint.status === 'fast_close_pending' && fastClose.requestedAt && !fastClose.gpvDeclinedAt && (
        <div style={{ background: `${colors.statusYellow}10`, borderRadius: 8, padding: '0.75rem' }}>
          <Label>Fast-Close Requested — Awaiting GPV Payment</Label>
          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
            <InfoItem label="Requested by" value={fastClose.requestedBy} />
            <InfoItem label="Amount requested" value={fastClose.compensationRequested != null ? `€${fastClose.compensationRequested.toLocaleString()}` : '—'} />
          </div>
          {fastClose.note && <p style={{ color: colors.textMuted, fontSize: '0.82rem', margin: '0.5rem 0 0' }}>{fastClose.note}</p>}
          {fastClose.negotiations?.length > 0 && (
            <details style={{ marginTop: '0.5rem' }}>
              <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: colors.textMuted }}>Negotiation history ({fastClose.negotiations.length})</summary>
              <div style={{ marginTop: '0.4rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                {fastClose.negotiations.map((n, i) => (
                  <div key={i} style={{ fontSize: '0.78rem', color: colors.textMuted, display: 'flex', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 700, color: n.from === 'nin' ? colors.primary : colors.statusYellow }}>{n.from.toUpperCase()}</span>
                    {n.amount != null && <span>€{n.amount.toLocaleString()}</span>}
                    {n.note && <span style={{ color: colors.textDim }}>— {n.note}</span>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Fast-close: NIN confirms receipt — show amount prominently */}
      {complaint.status === 'fast_close_confirmed' && (
        <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '1rem', background: `${colors.success}10`, borderRadius: 8, padding: '0.85rem' }}>
          <Label style={{ color: colors.success }}>Payment Confirmed by Supplier</Label>
          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', margin: '0.5rem 0 0.75rem' }}>
            <div>
              <Label>Amount</Label>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: colors.success, marginTop: '0.2rem' }}>
                €{(fastClose.compensationRequested || 0).toLocaleString()}
              </div>
            </div>
            <div>
              <Label>Confirmed by</Label>
              <div style={{ fontSize: '0.88rem', color: colors.textBody, marginTop: '0.2rem' }}>{fastClose.paymentConfirmedBy || '—'}</div>
            </div>
          </div>
          {fastClose.paymentNote && <p style={{ color: colors.textMuted, fontSize: '0.82rem', margin: '0 0 0.75rem', fontStyle: 'italic' }}>"{fastClose.paymentNote}"</p>}
          <button onClick={handleFastCloseReceived} disabled={saving} className="ni-button ni-button--primary">
            {saving ? 'Confirming…' : `Confirm €${(fastClose.compensationRequested || 0).toLocaleString()} Received & Close Case`}
          </button>
        </div>
      )}

      {/* Simple advance — draft→submitted only; monitoring/close handled in other tabs */}
      {complaint.status === 'draft' && waitingFor === 'nin' && (
        <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '1rem', marginTop: '0.5rem' }}>
          <textarea value={advanceNote} onChange={e => setAdvanceNote(e.target.value)}
            className="ni-input" rows={2} placeholder="Add a note (optional)..."
            style={{ resize: 'vertical', marginTop: '0.4rem', marginBottom: '0.5rem' }} />
          <button onClick={() => handleAdvance()} disabled={saving} className="ni-button ni-button--primary">
            {saving ? 'Processing…' : 'Submit to supplier'}
          </button>
        </div>
      )}

      {/* Archive confirmation for closed complaints */}
      {complaint.status === 'closed' && (
        <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '1rem' }}>
          {!showArchiveConfirm ? (
            <button onClick={() => setShowArchiveConfirm(true)} className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }}>
              Archive complaint
            </button>
          ) : (
            <div style={{ background: `${colors.textDim}10`, borderRadius: 8, padding: '0.85rem', border: `1px solid ${colors.panelBorder}` }}>
              <Label>Confirm archive</Label>
              <p style={{ color: colors.textMuted, fontSize: '0.82rem', margin: '0.35rem 0 0.65rem' }}>
                Once archived, this complaint will be read-only. All documentation will be preserved. This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => handleAdvance('archived')} disabled={saving} className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }}>
                  {saving ? 'Archiving…' : 'Yes, archive complaint'}
                </button>
                <button onClick={() => setShowArchiveConfirm(false)} className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick-nav */}
      {complaint.status === '8d_report' && complaint.eightDReport?.submittedAt && (
        <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '0.75rem' }}>
          <button onClick={() => onTabChange('8d_report')} className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }}>
            Review 8D Report →
          </button>
        </div>
      )}
      {complaint.status === 'corrective_action' && complaint.correctiveAction?.submittedAt && (
        <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '0.75rem' }}>
          <button onClick={() => onTabChange('corrective_action')} className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }}>
            Review Corrective Action Plan →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Info Request Card ─────────────────────────────────────────────────────────

function InfoRequestCard({ request, complaintId, apiCall, onUpdated, saving }) {
  const [resolvedNote, setResolvedNote] = useState('');
  const [resolvedFiles, setResolvedFiles] = useState([]);
  const [checkedTasks, setCheckedTasks] = useState([]);

  const toggleTask = (id) => setCheckedTasks(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleResolve = async () => {
    const fd = new FormData();
    fd.append('requestId', request.id);
    fd.append('resolvedNote', resolvedNote);
    checkedTasks.forEach(id => fd.append('completedTaskIds', id));
    resolvedFiles.forEach(f => fd.append('files', f));
    const d = await apiCall(`/complaints/${complaintId}/info-resolve`, 'POST', fd, true);
    if (d) onUpdated(d.complaint);
  };

  return (
    <div style={{ background: `${colors.statusYellow}10`, border: `1px solid ${colors.statusYellow}40`, borderRadius: 8, padding: '0.85rem' }}>
      <Label style={{ color: colors.statusYellow }}>Supplier Needs More Information</Label>
      <p style={{ color: colors.textBody, fontSize: '0.88rem', margin: '0.4rem 0 0.75rem', whiteSpace: 'pre-wrap' }}>{request.description}</p>
      {request.requestFiles?.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <FileList files={request.requestFiles} label="Supplier attachments" />
        </div>
      )}
      {request.tasks?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.75rem' }}>
          {request.tasks.map(t => (
            <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: colors.textBody }}>
              <input type="checkbox" checked={checkedTasks.includes(t.id)} onChange={() => toggleTask(t.id)} />
              <span style={{ textDecoration: checkedTasks.includes(t.id) ? 'line-through' : 'none', color: checkedTasks.includes(t.id) ? colors.textDim : colors.textBody }}>{t.text}</span>
            </label>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <div style={{ flex: '2 1 200px' }}>
          <Label>Your response</Label>
          <textarea value={resolvedNote} onChange={e => setResolvedNote(e.target.value)} className="ni-input" rows={2} placeholder="Provide the requested information..." style={{ resize: 'vertical', marginTop: '0.3rem' }} />
        </div>
        <div style={{ flex: '1 1 140px' }} tabIndex={0} onPaste={e => handleImagePaste(e, setResolvedFiles)}>
          <Label>Attachments</Label>
          <input type="file" multiple onChange={e => setResolvedFiles(Array.from(e.target.files))} className="ni-input" style={{ padding: '0.3rem', marginTop: '0.3rem' }} />
          <div style={{ fontSize: '0.72rem', color: colors.textMuted, marginTop: '0.2rem' }}>
            {resolvedFiles.length > 0
              ? `${resolvedFiles.length} file(s) selected`
              : 'Or click here and paste a screenshot.'}
          </div>
        </div>
      </div>
      <button onClick={handleResolve} disabled={saving} className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }}>
        {saving ? 'Sending…' : 'Respond & Mark Resolved'}
      </button>
    </div>
  );
}

// ── 8D Report ─────────────────────────────────────────────────────────────────

function EightDTab({ complaint, apiCall, onUpdated, saving }) {
  const r = complaint.eightDReport || {};
  const review = complaint.eightDReview || {};
  const isSubmitted = !!r.submittedAt;
  const [needsMoreNote, setNeedsMoreNote] = useState('');
  const [showNeedsMore, setShowNeedsMore] = useState(false);

  const handleAccept = async () => {
    const d = await apiCall(`/complaints/${complaint._id}/8d-review`, 'POST', { action: 'accept' });
    if (d) onUpdated(d.complaint);
  };
  const handleNeedsMore = async () => {
    if (!needsMoreNote.trim()) return;
    const d = await apiCall(`/complaints/${complaint._id}/8d-review`, 'POST', { action: 'needs_more', note: needsMoreNote });
    if (d) { onUpdated(d.complaint); setShowNeedsMore(false); setNeedsMoreNote(''); }
  };

  const D_LABELS = [
    ['d1_team','D1 — Team'],['d2_problem','D2 — Problem Statement'],
    ['d3_containment','D3 — Containment'],['d4_rootCause','D4 — Root Cause'],
    ['d5_correctiveActions','D5 — Corrective Actions'],['d6_implementation','D6 — Implementation'],
    ['d7_prevention','D7 — Prevention'],['d8_closure','D8 — Sign-off'],
  ];
  const hasContent = D_LABELS.some(([k]) => r[k]);

  // Derive sub-state
  const subState =
    review.status === 'accepted' ? 'done' :
    review.status === 'needs_more' && !r.resubmittedAt && !review.supplierDeclined ? 'waiting_supplier' :
    review.supplierDeclined && review.status !== 'accepted' ? 'supplier_declined' :
    isSubmitted ? 'review_needed' : 'not_submitted';

  if (subState === 'not_submitted') {
    return (
      <div style={emptyState}>
        {complaint.status === '8d_report'
          ? <><div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>⏳</div><p style={{ color:colors.textMuted, fontSize:'0.9rem' }}>8D report requested — waiting for the supplier to submit via their portal.</p></>
          : <p style={{ color:colors.textDim }}>8D phase has not started yet.</p>}
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>

      {/* ── Status banner ── */}
      {subState === 'done' && (
        <StatusBanner type="success" icon="✓" title="8D Report Accepted" detail="Corrective action phase has started." />
      )}
      {subState === 'waiting_supplier' && (
        <StatusBanner type="waiting" icon="⏳" title="Waiting for Supplier" detail={`You requested additional detail: "${review.needsMoreNote}"`} />
      )}
      {subState === 'supplier_declined' && (
        <StatusBanner type="warn" icon="!" title="Supplier Declined to Provide More Detail" detail={review.supplierDeclinedNote || 'No explanation given.'} />
      )}
      {subState === 'review_needed' && (
        <StatusBanner type="action" icon="⚡" title="Review Required" detail={r.resubmittedAt ? `Supplier resubmitted on ${new Date(r.resubmittedAt).toLocaleDateString('en-CA')}. Review the updated report below.` : 'Supplier has submitted the 8D report. Review and accept or request more detail.'} />
      )}

      {/* ── Submission meta ── */}
      <div style={{ display:'flex', gap:'1.5rem', flexWrap:'wrap', padding:'0.75rem', background:colors.surfaceMuted, borderRadius:8 }}>
        <InfoItem label="Submitted by" value={r.submittedBy} />
        <InfoItem label="Submitted" value={new Date(r.submittedAt).toLocaleDateString('en-CA')} />
        {r.resubmittedAt && <InfoItem label="Resubmitted" value={new Date(r.resubmittedAt).toLocaleDateString('en-CA')} />}
      </div>

      {/* ── Report content ── */}
      {r.summary && (
        <div>
          <Label>Summary / Notes</Label>
          <p style={{ color:colors.textBody, fontSize:'0.88rem', margin:'0.4rem 0 0', whiteSpace:'pre-wrap', background:colors.surfaceMuted, padding:'0.75rem', borderRadius:6 }}>{r.summary}</p>
        </div>
      )}
      <FileList files={r.files} label="Report Files" />
      {hasContent && (
        <details style={{ background:colors.surfaceMuted, borderRadius:8, padding:'0.75rem' }}>
          <summary style={{ cursor:'pointer', fontSize:'0.78rem', color:colors.textMuted, fontWeight:600 }}>Detailed 8D fields</summary>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.65rem', marginTop:'0.65rem' }}>
            {D_LABELS.map(([key, label]) => r[key] ? (
              <div key={key}>
                <Label>{label}</Label>
                <p style={{ color:colors.textBody, fontSize:'0.85rem', margin:'0.25rem 0 0', whiteSpace:'pre-wrap', background:colors.surface, padding:'0.5rem 0.65rem', borderRadius:6 }}>{r[key]}</p>
              </div>
            ) : null)}
          </div>
        </details>
      )}

      {/* ── Action block — only shown when NIN needs to act ── */}
      {(subState === 'review_needed' || subState === 'supplier_declined') && !showNeedsMore && (
        <div style={actionBlock}>
          <div style={{ fontSize:'0.75rem', color:colors.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'0.75rem' }}>
            {subState === 'supplier_declined' ? 'Supplier declined — decide how to proceed' : 'Your decision'}
          </div>
          <div style={{ display:'flex', gap:'0.65rem', flexWrap:'wrap' }}>
            <button onClick={handleAccept} disabled={saving} className="ni-button ni-button--primary">
              {saving ? 'Accepting…' : '✓ Accept Report — Move to Corrective Action'}
            </button>
            <button onClick={() => setShowNeedsMore(true)} className="ni-button ni-button--ghost" style={{ fontSize:'0.82rem' }}>
              Request more detail
            </button>
          </div>
        </div>
      )}
      {showNeedsMore && (
        <div style={{ ...actionBlock, borderColor: colors.statusYellow+'60', background:`${colors.statusYellow}06` }}>
          <Label style={{ color:colors.statusYellow }}>What additional information do you need?</Label>
          <p style={{ color:colors.textMuted, fontSize:'0.78rem', margin:'0.25rem 0 0.6rem' }}>The supplier can resubmit with more detail, or decline if they consider the report complete.</p>
          <textarea value={needsMoreNote} onChange={e => setNeedsMoreNote(e.target.value)} className="ni-input" rows={3}
            placeholder="Describe what is missing or unclear..." style={{ resize:'vertical', marginBottom:'0.5rem' }} />
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={handleNeedsMore} disabled={saving || !needsMoreNote.trim()} className="ni-button ni-button--primary">{saving ? 'Sending…' : 'Send Request'}</button>
            <button onClick={() => setShowNeedsMore(false)} className="ni-button ni-button--ghost">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Corrective Action ─────────────────────────────────────────────────────────

function CorrectiveActionTab({ complaint, apiCall, onUpdated, saving }) {
  const ca = complaint.correctiveAction || {};
  const review = complaint.caReview || {};
  const isSubmitted = !!ca.submittedAt;
  const [challengeLots, setChallengeLots] = useState(ca.proposedLotsPerMonth || '');
  const [challengeTotal, setChallengeTotal] = useState(ca.proposedTotalLots || '');
  const [challengeNote, setChallengeNote] = useState('');
  const [showChallenge, setShowChallenge] = useState(false);

  const proposedMonths = ca.proposedLotsPerMonth && ca.proposedTotalLots
    ? Math.ceil(ca.proposedTotalLots / ca.proposedLotsPerMonth) : null;

  const handleAccept = async () => {
    const d = await apiCall(`/complaints/${complaint._id}/ca-review`, 'POST', { action: 'accept' });
    if (d) onUpdated(d.complaint);
  };
  const handleChallenge = async () => {
    const d = await apiCall(`/complaints/${complaint._id}/ca-review`, 'POST', {
      action: 'challenge',
      challengeLotsPerMonth: challengeLots,
      challengeTotalLots: challengeTotal,
      challengeNote,
    });
    if (d) { onUpdated(d.complaint); setShowChallenge(false); }
  };

  if (!isSubmitted) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        {complaint.status === 'corrective_action' ? (
          <>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
            <p style={{ color: colors.textMuted, fontSize: '0.88rem' }}>Corrective action plan has been requested. Waiting for the supplier to submit it.</p>
          </>
        ) : (
          <p style={{ color: colors.textDim, fontSize: '0.85rem' }}>Not started yet.</p>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <InfoItem label="Submitted by" value={ca.submittedBy} />
        <InfoItem label="Submitted at" value={new Date(ca.submittedAt).toLocaleDateString('en-CA')} />
        {ca.targetDate && <InfoItem label="Target date" value={new Date(ca.targetDate).toLocaleDateString('en-CA')} />}
      </div>
      <div>
        <Label>Corrective Action Plan</Label>
        <p style={{ color: colors.textBody, fontSize: '0.88rem', margin: '0.35rem 0 0', whiteSpace: 'pre-wrap', background: colors.surfaceMuted, padding: '0.75rem', borderRadius: 6 }}>
          {ca.description || <span style={{ color: colors.textDim, fontStyle: 'italic' }}>No description</span>}
        </p>
      </div>
      <FileList files={ca.files} label="Attached Files" />

      {/* Proposed lot schedule */}
      {(ca.proposedLotsPerMonth || ca.proposedTotalLots) && (
        <div style={{ background: `${colors.primary}10`, border: `1px solid ${colors.primary}25`, borderRadius: 8, padding: '0.85rem' }}>
          <Label>Supplier's Proposed Monitoring Schedule</Label>
          <Grid style={{ marginTop: '0.5rem' }}>
            <InfoItem label="Lots / month" value={ca.proposedLotsPerMonth} />
            <InfoItem label="Total lots" value={ca.proposedTotalLots} />
            {proposedMonths && <InfoItem label="Estimated duration" value={`~${proposedMonths} months`} />}
          </Grid>
        </div>
      )}

      {/* Challenge response from GPV */}
      {review.supplierResponseAt && (
        <div style={{ background: `${colors.primary}08`, border: `1px solid ${colors.primary}20`, borderRadius: 8, padding: '0.75rem' }}>
          <Label>Supplier Response to Challenge</Label>
          <Grid style={{ marginTop: '0.4rem' }}>
            <InfoItem label="Lots / month" value={review.supplierResponseLotsPerMonth} />
            <InfoItem label="Total lots" value={review.supplierResponseTotalLots} />
            {review.supplierResponseLotsPerMonth && review.supplierResponseTotalLots && (
              <InfoItem label="Duration" value={`~${Math.ceil(review.supplierResponseTotalLots / review.supplierResponseLotsPerMonth)} months`} />
            )}
          </Grid>
          {review.supplierResponseNote && <p style={{ color: colors.textMuted, fontSize: '0.82rem', margin: '0.5rem 0 0' }}>{review.supplierResponseNote}</p>}
        </div>
      )}

      {/* Challenge details */}
      {review.status === 'challenged' && (
        <div style={{ background: `${colors.statusYellow}10`, border: `1px solid ${colors.statusYellow}30`, borderRadius: 8, padding: '0.75rem' }}>
          <Label style={{ color: colors.statusYellow }}>You challenged the monitoring schedule</Label>
          <Grid style={{ marginTop: '0.4rem' }}>
            <InfoItem label="Proposed lots / month" value={review.challengeLotsPerMonth} />
            <InfoItem label="Proposed total lots" value={review.challengeTotalLots} />
          </Grid>
          <p style={{ color: colors.textMuted, fontSize: '0.82rem', margin: '0.5rem 0 0' }}>Waiting for supplier to respond.</p>
        </div>
      )}

      {/* Review actions */}
      {complaint.status === 'corrective_action' && review.status !== 'accepted' && review.status !== 'challenged' && !showChallenge && (
        <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '1rem', display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
          <button onClick={handleAccept} disabled={saving} className="ni-button ni-button--primary">
            {saving ? 'Starting…' : '✓ Accept Plan — Start Monitoring'}
          </button>
          <button onClick={() => setShowChallenge(true)} className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }}>
            Propose different schedule
          </button>
        </div>
      )}
      {/* After supplier responds to challenge, NIN can accept */}
      {complaint.status === 'corrective_action' && review.supplierResponseAt && review.status !== 'accepted' && !showChallenge && (
        <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '1rem', display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
          <button onClick={handleAccept} disabled={saving} className="ni-button ni-button--primary">
            {saving ? 'Starting…' : '✓ Accept Supplier Response — Start Monitoring'}
          </button>
          <button onClick={() => setShowChallenge(true)} className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }}>
            Propose again
          </button>
        </div>
      )}
      {showChallenge && (
        <div style={{ background: `${colors.statusYellow}08`, borderRadius: 8, padding: '0.85rem', border: `1px solid ${colors.statusYellow}30` }}>
          <Label style={{ color: colors.statusYellow }}>Propose Different Monitoring Schedule</Label>
          <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', margin: '0.65rem 0' }}>
            <div style={{ flex: '1 1 120px' }}>
              <Label>Lots / month</Label>
              <input type="number" value={challengeLots} onChange={e => setChallengeLots(e.target.value)} className="ni-input" style={{ marginTop: '0.3rem' }} min={1} />
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <Label>Total lots</Label>
              <input type="number" value={challengeTotal} onChange={e => setChallengeTotal(e.target.value)} className="ni-input" style={{ marginTop: '0.3rem' }} min={1} />
            </div>
            {challengeLots && challengeTotal && (
              <div style={{ flex: '1 1 120px', alignSelf: 'flex-end', paddingBottom: '0.4rem' }}>
                <span style={{ fontSize: '0.82rem', color: colors.textMuted }}>~{Math.ceil(Number(challengeTotal) / Number(challengeLots))} months</span>
              </div>
            )}
          </div>
          <Label>Note (optional)</Label>
          <input value={challengeNote} onChange={e => setChallengeNote(e.target.value)} className="ni-input" placeholder="Reason for different schedule..." style={{ marginTop: '0.3rem', marginBottom: '0.5rem' }} />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleChallenge} disabled={saving || !challengeLots || !challengeTotal} className="ni-button ni-button--primary">{saving ? 'Sending…' : 'Send Proposal'}</button>
            <button onClick={() => setShowChallenge(false)} className="ni-button ni-button--ghost">Cancel</button>
          </div>
        </div>
      )}
      {review.status === 'accepted' && (
        <div style={{ background: `${colors.success}12`, border: `1px solid ${colors.success}30`, borderRadius: 8, padding: '0.65rem 0.85rem', color: colors.success, fontSize: '0.82rem', fontWeight: 600 }}>
          ✓ Corrective action plan accepted — Monitoring phase started automatically
        </div>
      )}
    </div>
  );
}

// ── Monitoring ────────────────────────────────────────────────────────────────

function MonitoringTab({ complaint, apiCall, onUpdated, saving, onTabChange }) {
  const mon = complaint.monitoring || {};
  const isStarted = !!mon.startedAt;
  const [expandedLot, setExpandedLot] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [verifyNote, setVerifyNote] = useState('');
  const [requestingFailure, setRequestingFailure] = useState(null);
  const [failureRequestNote, setFailureRequestNote] = useState('');
  const [lotNotes, setLotNotes] = useState('');
  const [lotResult, setLotResult] = useState('pass');
  const [lotFiles, setLotFiles] = useState([]);

  const handleSubmitLot = async (lotNumber) => {
    const fd = new FormData();
    fd.append('lotNumber', lotNumber);
    fd.append('result', lotResult);
    fd.append('notes', lotNotes);
    lotFiles.forEach(f => fd.append('files', f));
    const d = await apiCall(`/complaints/${complaint._id}/monitoring/lot`, 'POST', fd, true);
    if (d) { onUpdated(d.complaint); setLotNotes(''); setLotFiles([]); }
  };

  const handleVerify = async (lotNumber) => {
    const d = await apiCall(`/complaints/${complaint._id}/monitoring/lot-verify`, 'POST', { lotNumber, note: verifyNote });
    if (d) { onUpdated(d.complaint); setVerifying(null); setVerifyNote(''); }
  };

  const handleRequestFailureAction = async (lotNumber) => {
    const d = await apiCall(`/complaints/${complaint._id}/monitoring/lot-failure-request`, 'POST', { lotNumber, note: failureRequestNote });
    if (d) { onUpdated(d.complaint); setRequestingFailure(null); setFailureRequestNote(''); }
  };

  const handleAcceptFailure = async (lotNumber) => {
    const d = await apiCall(`/complaints/${complaint._id}/monitoring/lot-failure-accept`, 'POST', { lotNumber });
    if (d) onUpdated(d.complaint);
  };

  const lots = mon.lots || [];
  const passed = lots.filter(l => l.result === 'pass').length;
  const failed = lots.filter(l => l.result === 'fail').length;
  const nextLotNumber = lots.length + 1;
  const scheduleComplete = Boolean(mon.totalLots && lots.length >= mon.totalLots);
  const progress = mon.totalLots ? ((passed + failed) / mon.totalLots) : 0;

  if (!isStarted) {
    return (
      <div style={emptyState}>
        <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
        <p style={{ color: colors.textMuted, fontSize: '0.88rem' }}>Monitoring starts automatically once you accept the corrective action plan.</p>
      </div>
    );
  }

  const unverified = lots.filter(l => !l.verifiedAt).length;
  const awaitingFailureFix = lots.filter(l => l.failureAction?.status === 'requested').length;
  const failureAnswered = lots.filter(l => l.failureAction?.status === 'responded').length;
  const scheduleDone = Boolean(mon.totalLots && lots.length >= mon.totalLots);

  // Say plainly what is outstanding. Monitoring runs for months, so "action
  // required" on its own tells nobody anything.
  let outstanding = null;
  if (failureAnswered) {
    outstanding = { tone: colors.statusYellow, text: `${failureAnswered} failed lot response${failureAnswered > 1 ? 's' : ''} to review and accept.` };
  } else if (unverified) {
    outstanding = { tone: colors.statusYellow, text: `${unverified} submitted lot${unverified > 1 ? 's' : ''} still to verify.` };
  } else if (awaitingFailureFix) {
    outstanding = { tone: colors.textMuted, text: `Waiting on the supplier's corrective action for ${awaitingFailureFix} failed lot${awaitingFailureFix > 1 ? 's' : ''}.` };
  } else if (scheduleDone) {
    outstanding = { tone: colors.success, text: `All ${mon.totalLots} lots received and verified — nothing outstanding. Close the complaint with a final report.`, close: true };
  } else {
    outstanding = { tone: colors.textMuted, text: `Waiting on the supplier — ${lots.length} of ${mon.totalLots} lots submitted so far.` };
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{
        background: `${outstanding.tone}12`,
        border: `1px solid ${outstanding.tone}40`,
        borderRadius: 8,
        padding: '0.7rem 0.85rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        flexWrap: 'wrap',
      }}>
        <span style={{ color: outstanding.tone, fontSize: '0.85rem', fontWeight: 600, flex: 1, minWidth: 220 }}>
          {outstanding.text}
        </span>
        {outstanding.close && (
          <button
            type="button"
            onClick={() => onTabChange && onTabChange('final_report')}
            className="ni-button ni-button--primary"
            style={{ fontSize: '0.8rem', flexShrink: 0 }}
          >
            Go to Final Report →
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <InfoItem label="Lots/Month" value={mon.lotsPerMonth} />
        <InfoItem label="Total Lots" value={mon.totalLots} />
        <InfoItem label="Started" value={new Date(mon.startedAt).toLocaleDateString('en-CA')} />
        <InfoItem label="Passed" value={<span style={{ color: colors.success, fontWeight: 700 }}>{passed}</span>} />
        <InfoItem label="Failed" value={<span style={{ color: failed > 0 ? colors.danger : colors.textMuted, fontWeight: 700 }}>{failed}</span>} />
        <InfoItem label="Pending" value={<span style={{ color: colors.textMuted }}>{mon.totalLots - passed - failed}</span>} />
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: colors.textDim, marginBottom: '0.3rem' }}>
          <span>{Math.round(progress * 100)}% complete</span>
          <span>{passed + failed} / {mon.totalLots} lots done</span>
        </div>
        <div style={{ background: colors.surfaceMuted, borderRadius: 6, height: 8, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress * 100}%`, background: failed > 0 ? colors.statusYellow : colors.success, transition: 'width 0.3s' }} />
        </div>
      </div>

      {lots.some(l => l.failureAction?.status === 'responded') && (
        <StatusBanner type="action" icon="⚡" title="Supplier responded to a failed lot" detail="Review and accept their corrective actions in the lot list below." />
      )}
      {lots.some(l => l.failureAction?.status === 'requested') && !lots.some(l => l.failureAction?.status === 'responded') && (
        <StatusBanner type="waiting" icon="⏳" title="Awaiting supplier corrective action" detail="The supplier has been asked to respond to a failed lot." />
      )}

      {!scheduleComplete && complaint.status === 'monitoring' && (
        <div style={{ ...actionBlock, borderColor: `${colors.primary}40` }}>
          <div style={{ fontSize: '0.75rem', color: colors.textDim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.65rem' }}>Log Lot {nextLotNumber}</div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
            <div>
              <Label>Result</Label>
              <select value={lotResult} onChange={e => setLotResult(e.target.value)} className="ni-input" style={{ marginTop: '0.3rem', width: 'auto' }}>
                <option value="pass">Pass</option>
                <option value="fail">Fail</option>
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <Label>Notes (optional)</Label>
              <input value={lotNotes} onChange={e => setLotNotes(e.target.value)} className="ni-input" placeholder="Observations..." style={{ marginTop: '0.3rem' }} />
            </div>
          </div>
          <Label>Attachments (optional)</Label>
          <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.zip" onChange={e => setLotFiles(Array.from(e.target.files))} className="ni-input" style={{ padding: '0.35rem', marginTop: '0.3rem', marginBottom: '0.5rem' }} />
          {lotFiles.length > 0 && <div style={{ fontSize: '0.72rem', color: colors.textMuted, marginBottom: '0.5rem' }}>{lotFiles.length} file(s) selected</div>}
          <button onClick={() => handleSubmitLot(nextLotNumber)} disabled={saving} className="ni-button ni-button--primary">
            {saving ? 'Saving…' : `Log Lot ${nextLotNumber}`}
          </button>
        </div>
      )}

      <div>
        <Label>Lot Results</Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
          {lots.map(lot => {
            const fa = lot.failureAction || {};
            const isExpanded = expandedLot === lot.lotNumber;
            const borderColor = lot.result === 'fail' && fa.status === 'responded' ? colors.danger
              : lot.result === 'fail' && fa.status !== 'accepted' ? colors.danger + '60'
              : colors.panelBorder;
            return (
              <div key={lot.lotNumber} style={{ background: colors.surface, borderRadius: 8, border: `1px solid ${borderColor}`, overflow: 'hidden' }}>
                {/* Row header — click to expand */}
                <div onClick={() => setExpandedLot(isExpanded ? null : lot.lotNumber)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.6rem 0.75rem', cursor: 'pointer', flexWrap: 'wrap' }}>
                  <span style={{ color: colors.textDim, minWidth: 28, fontWeight: 600, fontSize: '0.82rem' }}>#{lot.lotNumber}</span>
                  <span style={{ fontWeight: 700, fontSize: '0.82rem', color: lot.result === 'pass' ? colors.success : lot.result === 'fail' ? colors.danger : colors.textDim }}>
                    {lot.result.toUpperCase()}
                  </span>
                  {lot.verifiedAt && <span style={{ fontSize: '0.63rem', background: `${colors.success}20`, color: colors.success, borderRadius: 4, padding: '0.1rem 0.4rem', fontWeight: 700 }}>✓ VERIFIED</span>}
                  {lot.result === 'fail' && fa.status === 'responded' && <span style={{ fontSize: '0.63rem', background: `${colors.danger}20`, color: colors.danger, borderRadius: 4, padding: '0.1rem 0.4rem', fontWeight: 700 }}>ACTION NEEDED</span>}
                  {lot.result === 'fail' && fa.status === 'requested' && <span style={{ fontSize: '0.63rem', background: `${colors.statusYellow}20`, color: colors.statusYellow, borderRadius: 4, padding: '0.1rem 0.4rem', fontWeight: 700 }}>AWAITING SUPPLIER</span>}
                  {lot.result === 'fail' && fa.status === 'accepted' && <span style={{ fontSize: '0.63rem', background: `${colors.success}20`, color: colors.success, borderRadius: 4, padding: '0.1rem 0.4rem', fontWeight: 700 }}>RESOLVED</span>}
                  {lot.submittedBy && <span style={{ color: colors.textDim, fontSize: '0.72rem' }}>{lot.submittedBy}</span>}
                  {lot.submittedAt && <span style={{ color: colors.textDim, fontSize: '0.7rem' }}>{new Date(lot.submittedAt).toLocaleDateString('en-CA')}</span>}
                  {lot.notes && <span style={{ color: colors.textMuted, flex: 1, minWidth: 80, fontSize: '0.8rem' }}>{lot.notes}</span>}
                  <span style={{ marginLeft: 'auto', color: colors.textDim, fontSize: '0.7rem', flexShrink: 0 }}>{isExpanded ? '▲' : '▼'}</span>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div style={{ padding: '0.85rem', borderTop: `1px solid ${colors.panelBorder}`, background: colors.surfaceMuted, display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                    <Grid>
                      <InfoItem label="Result" value={<span style={{ fontWeight: 700, color: lot.result === 'pass' ? colors.success : colors.danger }}>{lot.result.toUpperCase()}</span>} />
                      <InfoItem label="Submitted by" value={lot.submittedBy || '—'} />
                      <InfoItem label="Submitted" value={lot.submittedAt ? new Date(lot.submittedAt).toLocaleDateString('en-CA') : '—'} />
                      {lot.notes && <InfoItem label="Notes" value={lot.notes} />}
                    </Grid>
                    <FileList files={lot.files} label="Attached Files" />

                    {/* Verification */}
                    {lot.verifiedAt ? (
                      <div style={{ fontSize: '0.78rem', color: colors.success, background: `${colors.success}10`, borderRadius: 6, padding: '0.5rem 0.65rem' }}>
                        ✓ Verified by {lot.verifiedBy} on {new Date(lot.verifiedAt).toLocaleDateString('en-CA')}
                        {lot.verificationNote && <span style={{ color: colors.textMuted }}> — {lot.verificationNote}</span>}
                      </div>
                    ) : verifying === lot.lotNumber ? (
                      <div style={{ background: colors.surface, borderRadius: 6, padding: '0.65rem', border: `1px solid ${colors.panelBorder}` }}>
                        <Label>Verification note (optional)</Label>
                        <input value={verifyNote} onChange={e => setVerifyNote(e.target.value)} className="ni-input" placeholder="Confirm you have reviewed this lot..." style={{ marginTop: '0.3rem', marginBottom: '0.5rem' }} />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button onClick={() => handleVerify(lot.lotNumber)} disabled={saving} className="ni-button ni-button--primary" style={{ fontSize: '0.8rem' }}>{saving ? 'Saving…' : 'Confirm Verification'}</button>
                          <button onClick={() => setVerifying(null)} className="ni-button ni-button--ghost" style={{ fontSize: '0.8rem' }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => setVerifying(lot.lotNumber)} className="ni-button ni-button--ghost" style={{ fontSize: '0.8rem', alignSelf: 'flex-start' }}>
                        Verify / Acknowledge Lot
                      </button>
                    )}

                    {/* Failure action flow */}
                    {lot.result === 'fail' && (
                      <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '0.65rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Label style={{ color: colors.danger }}>Failed Lot — Corrective Action</Label>
                        {!fa.status && requestingFailure !== lot.lotNumber && (
                          <button onClick={() => setRequestingFailure(lot.lotNumber)} className="ni-button ni-button--ghost" style={{ fontSize: '0.8rem', borderColor: `${colors.danger}60`, color: colors.danger, alignSelf: 'flex-start' }}>
                            Request Supplier Corrective Action
                          </button>
                        )}
                        {requestingFailure === lot.lotNumber && (
                          <div style={{ background: colors.surface, borderRadius: 6, padding: '0.65rem', border: `1px solid ${colors.danger}40` }}>
                            <Label style={{ color: colors.danger }}>What corrective actions are required?</Label>
                            <textarea value={failureRequestNote} onChange={e => setFailureRequestNote(e.target.value)} className="ni-input" rows={2}
                              placeholder="Describe what actions the supplier must take..." style={{ resize: 'vertical', marginTop: '0.35rem', marginBottom: '0.5rem' }} />
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button onClick={() => handleRequestFailureAction(lot.lotNumber)} disabled={saving} className="ni-button ni-button--primary" style={{ fontSize: '0.8rem' }}>{saving ? 'Sending…' : 'Send Request'}</button>
                              <button onClick={() => setRequestingFailure(null)} className="ni-button ni-button--ghost" style={{ fontSize: '0.8rem' }}>Cancel</button>
                            </div>
                          </div>
                        )}
                        {fa.status === 'requested' && (
                          <div style={{ background: `${colors.statusYellow}10`, border: `1px solid ${colors.statusYellow}30`, borderRadius: 6, padding: '0.6rem 0.75rem', fontSize: '0.82rem' }}>
                            <span style={{ fontWeight: 700, color: colors.statusYellow }}>⏳ Awaiting supplier response</span>
                            {fa.requestNote && <p style={{ color: colors.textMuted, margin: '0.3rem 0 0', fontSize: '0.8rem' }}>{fa.requestNote}</p>}
                          </div>
                        )}
                        {fa.status === 'responded' && (
                          <div style={{ background: `${colors.danger}08`, border: `1px solid ${colors.danger}30`, borderRadius: 6, padding: '0.75rem' }}>
                            <Label style={{ color: colors.danger }}>Supplier's Corrective Action Response</Label>
                            <p style={{ color: colors.textBody, fontSize: '0.85rem', margin: '0.4rem 0 0.5rem', whiteSpace: 'pre-wrap' }}>{fa.responseNote || 'No details provided.'}</p>
                            <FileList files={fa.responseFiles} label="Attachments" />
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.65rem', flexWrap: 'wrap', alignItems: 'center' }}>
                              <InfoItem label="Responded by" value={fa.respondedBy} />
                            </div>
                            <button onClick={() => handleAcceptFailure(lot.lotNumber)} disabled={saving} className="ni-button ni-button--primary" style={{ fontSize: '0.82rem', marginTop: '0.65rem' }}>
                              {saving ? 'Accepting…' : '✓ Accept Response — Continue Monitoring'}
                            </button>
                          </div>
                        )}
                        {fa.status === 'accepted' && (
                          <div style={{ fontSize: '0.78rem', color: colors.success, background: `${colors.success}10`, borderRadius: 6, padding: '0.5rem 0.65rem' }}>
                            ✓ Corrective action accepted by {fa.acceptedBy} on {fa.acceptedAt ? new Date(fa.acceptedAt).toLocaleDateString('en-CA') : '—'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Compensation ──────────────────────────────────────────────────────────────

const COMP_STATUS_LABELS_FULL = {
  pending: 'Pending — supplier has not responded',
  challenged: 'Challenged — supplier sent a counter-offer',
  partially_accepted: 'Partially Accepted',
  accepted: 'Accepted — awaiting payment',
  payment_sent: 'Payment Sent — awaiting NIN confirmation',
  payment_received: 'Payment Received — settled',
  rejected: 'Rejected',
};

function CompensationTab({ complaint, apiCall, onUpdated, saving }) {
  const comp = complaint.compensation || {};
  const [newStatus, setNewStatus] = useState(comp.status || 'pending');
  const [challengeNotes, setChallengeNotes] = useState(comp.challengeNotes || '');
  const [acceptedHours, setAcceptedHours] = useState(comp.acceptedHours ?? '');
  const [acceptedBom, setAcceptedBom] = useState(comp.acceptedBomCost ?? '');
  const isArchived = ['closed', 'archived'].includes(complaint.status);

  const handleSave = async () => {
    const d = await apiCall(`/complaints/${complaint._id}/compensation`, 'POST', {
      status: newStatus, challengeNotes,
      acceptedHours: acceptedHours !== '' ? Number(acceptedHours) : null,
      acceptedBomCost: acceptedBom !== '' ? Number(acceptedBom) : null,
    });
    if (d) onUpdated(d.complaint);
  };

  const handlePaymentReceived = async () => {
    const d = await apiCall(`/complaints/${complaint._id}/compensation-payment-received`, 'POST', {});
    if (d) onUpdated(d.complaint);
  };

  const statusColor = {
    pending: colors.textDim,
    challenged: colors.statusYellow,
    partially_accepted: colors.statusYellow,
    accepted: colors.primary,
    payment_sent: colors.statusYellow,
    payment_received: colors.success,
    rejected: colors.danger,
  }[comp.status] || colors.textDim;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* Status banner */}
      {comp.status === 'payment_sent' && (
        <StatusBanner type="action" icon="⚡" title="Supplier confirms payment was sent" detail="Confirm below once you have received the payment to complete this compensation claim." />
      )}
      {comp.status === 'payment_received' && (
        <StatusBanner type="success" icon="✓" title="Payment received and confirmed" detail={`Confirmed by ${comp.paymentReceivedBy} on ${comp.paymentReceivedAt ? new Date(comp.paymentReceivedAt).toLocaleDateString('en-CA') : '—'}`} />
      )}
      {comp.status === 'challenged' && (
        <StatusBanner type="warn" icon="!" title="Supplier challenged the claim" detail="Review their counter-offer below and update the accepted amounts." />
      )}

      {/* NIN claim */}
      <div style={{ background: colors.surfaceMuted, borderRadius: 8, padding: '0.85rem' }}>
        <Label>NIN Claim</Label>
        <Grid style={{ marginTop: '0.5rem' }}>
          <InfoItem label="Hours spent" value={comp.hoursSpent || 0} />
          <InfoItem label="Hourly rate" value={`€${comp.hourlyRate || 0}/h`} />
          <InfoItem label="BOM cost" value={`€${comp.bomCost || 0}`} />
          <InfoItem label="Total claimed" value={<strong style={{ color: colors.statusYellow, fontSize: '1rem' }}>€{(comp.totalClaimed || 0).toLocaleString()}</strong>} />
        </Grid>
      </div>

      {/* Supplier status */}
      <div style={{ background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 8, padding: '0.85rem' }}>
        <Label>Supplier Response</Label>
        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: statusColor, marginTop: '0.35rem' }}>
          {COMP_STATUS_LABELS_FULL[comp.status] || comp.status}
        </div>
        {comp.supplierAcceptedAt && (
          <div style={{ fontSize: '0.78rem', color: colors.textMuted, marginTop: '0.3rem' }}>
            Accepted by {comp.supplierAcceptedBy} on {new Date(comp.supplierAcceptedAt).toLocaleDateString('en-CA')}
          </div>
        )}
        {comp.paymentSentAt && (
          <div style={{ marginTop: '0.6rem' }}>
            <Label>Payment Sent</Label>
            <div style={{ fontSize: '0.85rem', color: colors.textBody, marginTop: '0.2rem' }}>
              {comp.paymentSentBy} on {new Date(comp.paymentSentAt).toLocaleDateString('en-CA')}
              {comp.paymentSentNote && <span style={{ color: colors.textMuted }}> — "{comp.paymentSentNote}"</span>}
            </div>
          </div>
        )}
      </div>

      {/* Supplier counter-offer */}
      {(comp.supplierCounterHours != null || comp.supplierCounterBom != null) && (
        <div style={{ background: `${colors.statusYellow}10`, border: `1px solid ${colors.statusYellow}30`, borderRadius: 8, padding: '0.85rem' }}>
          <Label style={{ color: colors.statusYellow }}>Supplier Counter-Offer</Label>
          <Grid style={{ marginTop: '0.5rem' }}>
            {comp.supplierCounterHours != null && <InfoItem label="Counter hours" value={comp.supplierCounterHours} />}
            {comp.supplierCounterBom != null && <InfoItem label="Counter BOM" value={`€${comp.supplierCounterBom}`} />}
            {comp.supplierCounterTotal != null && <InfoItem label="Counter total" value={<strong style={{ color: colors.statusYellow }}>€{comp.supplierCounterTotal.toLocaleString()}</strong>} />}
          </Grid>
          {comp.challengeNotes && <p style={{ color: colors.textMuted, fontSize: '0.82rem', margin: '0.5rem 0 0' }}>{comp.challengeNotes}</p>}
        </div>
      )}

      {/* Agreed / accepted total */}
      {comp.acceptedTotal != null && (
        <div style={{ background: `${colors.success}12`, borderRadius: 8, padding: '0.85rem', border: `1px solid ${colors.success}30` }}>
          <Label>Agreed Amount</Label>
          <Grid style={{ marginTop: '0.5rem' }}>
            {comp.acceptedHours != null && <InfoItem label="Accepted hours" value={comp.acceptedHours} />}
            {comp.acceptedBomCost != null && <InfoItem label="Accepted BOM" value={`€${comp.acceptedBomCost}`} />}
            <InfoItem label="Total agreed" value={<strong style={{ color: colors.success, fontSize: '1rem' }}>€{comp.acceptedTotal.toLocaleString()}</strong>} />
            {comp.acceptedAt && <InfoItem label="Agreed on" value={new Date(comp.acceptedAt).toLocaleDateString('en-CA')} />}
          </Grid>
        </div>
      )}

      {/* NIN: confirm payment received */}
      {comp.status === 'payment_sent' && !comp.paymentReceivedAt && (
        <div style={{ ...actionBlock, borderColor: `${colors.success}40` }}>
          <div style={{ fontSize: '0.75rem', color: colors.textDim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.65rem' }}>Your action</div>
          <p style={{ color: colors.textMuted, fontSize: '0.82rem', margin: '0 0 0.65rem' }}>The supplier has confirmed payment was sent. Confirm receipt to mark this compensation as settled.</p>
          <button onClick={handlePaymentReceived} disabled={saving} className="ni-button ni-button--primary">
            {saving ? 'Confirming…' : '✓ Confirm Payment Received'}
          </button>
        </div>
      )}

      {!isArchived && comp.status !== 'payment_received' && (
        <>
          <Divider label="Update Status" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>
              <Label>Compensation Status</Label>
              <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="ni-input" style={{ marginTop: '0.35rem' }}>
                {Object.entries(COMP_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label>Notes</Label>
              <textarea value={challengeNotes} onChange={e => setChallengeNotes(e.target.value)} className="ni-input" rows={2} style={{ resize: 'vertical', marginTop: '0.35rem' }} placeholder="Counter-offer details, negotiation notes..." />
            </div>
            {(newStatus === 'partially_accepted' || newStatus === 'accepted') && (
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 120px' }}>
                  <Label>Accepted Hours</Label>
                  <input type="number" value={acceptedHours} onChange={e => setAcceptedHours(e.target.value)} className="ni-input" style={{ marginTop: '0.35rem' }} min={0} />
                </div>
                <div style={{ flex: '1 1 120px' }}>
                  <Label>Accepted BOM (EUR)</Label>
                  <input type="number" value={acceptedBom} onChange={e => setAcceptedBom(e.target.value)} className="ni-input" style={{ marginTop: '0.35rem' }} min={0} />
                </div>
              </div>
            )}
            <button onClick={handleSave} disabled={saving} className="ni-button ni-button--primary" style={{ alignSelf: 'flex-start' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Final Report ──────────────────────────────────────────────────────────────

function FinalReportTab({ complaint, apiCall, onUpdated, saving }) {
  const fr = complaint.finalReport || {};
  const isReadOnly = ['closed', 'archived'].includes(complaint.status);
  const [summary, setSummary] = useState(fr.summary || '');
  const [riskLevel, setRiskLevel] = useState(fr.riskLevel || 'low');
  const [riskNotes, setRiskNotes] = useState(fr.riskNotes || '');
  const [recommendation, setRecommendation] = useState(fr.recommendation || '');
  const [recurrence, setRecurrence] = useState(fr.recurrenceLikelihood ?? 5);

  const handleClose = async () => {
    const d = await apiCall(`/complaints/${complaint._id}/close`, 'POST', {
      summary, riskLevel, riskNotes, recommendation, recurrenceLikelihood: recurrence
    });
    if (d) onUpdated(d.complaint);
  };

  const stageTimes = getStageTimes(complaint);
  const riskColor = riskLevel === 'high' ? colors.danger : riskLevel === 'medium' ? colors.statusYellow : colors.success;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {fr.generatedAt && (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <InfoItem label="Generated by" value={fr.generatedBy} />
          <InfoItem label="Generated at" value={new Date(fr.generatedAt).toLocaleDateString('en-CA')} />
        </div>
      )}

      {stageTimes.length > 0 && (
        <div style={{ background: colors.surfaceMuted, borderRadius: 8, padding: '0.85rem' }}>
          <Label>Total timeline</Label>
          <Grid style={{ marginTop: '0.5rem' }}>
            {stageTimes.map(t => <InfoItem key={t.label} label={t.label} value={t.value} />)}
          </Grid>
        </div>
      )}

      <div>
        <Label>Executive Summary</Label>
        {isReadOnly
          ? <p style={{ color: colors.textBody, fontSize: '0.88rem', margin: '0.4rem 0 0', whiteSpace: 'pre-wrap', background: colors.surfaceMuted, padding: '0.65rem 0.75rem', borderRadius: 6 }}>{fr.summary || <span style={{ color: colors.textDim, fontStyle: 'italic' }}>None</span>}</p>
          : <textarea value={summary} onChange={e => setSummary(e.target.value)} className="ni-input" rows={5} placeholder="Summary of the full complaint lifecycle, findings, and outcome..." style={{ resize: 'vertical', marginTop: '0.4rem' }} />
        }
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 120px' }}>
          <Label>Risk Level</Label>
          {isReadOnly
            ? <div style={{ fontSize: '0.88rem', color: riskColor, fontWeight: 700, marginTop: '0.35rem', textTransform: 'capitalize' }}>{fr.riskLevel || '—'}</div>
            : <select value={riskLevel} onChange={e => setRiskLevel(e.target.value)} className="ni-input" style={{ marginTop: '0.35rem' }}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
          }
        </div>
        <div style={{ flex: '2 1 200px' }}>
          <Label>Risk Notes</Label>
          {isReadOnly
            ? <div style={{ fontSize: '0.88rem', color: colors.textBody, marginTop: '0.35rem' }}>{fr.riskNotes || '—'}</div>
            : <input value={riskNotes} onChange={e => setRiskNotes(e.target.value)} className="ni-input" style={{ marginTop: '0.35rem' }} placeholder="Risk justification..." />
          }
        </div>
      </div>

      <div>
        <Label>Recommendation</Label>
        {isReadOnly
          ? <p style={{ color: colors.textBody, fontSize: '0.88rem', margin: '0.4rem 0 0', whiteSpace: 'pre-wrap', background: colors.surfaceMuted, padding: '0.65rem 0.75rem', borderRadius: 6 }}>{fr.recommendation || <span style={{ color: colors.textDim, fontStyle: 'italic' }}>None</span>}</p>
          : <textarea value={recommendation} onChange={e => setRecommendation(e.target.value)} className="ni-input" rows={3} placeholder="Future monitoring or process change recommendations..." style={{ resize: 'vertical', marginTop: '0.4rem' }} />
        }
      </div>

      <div>
        <Label>Recurrence Likelihood — <strong style={{ color: (isReadOnly ? fr.recurrenceLikelihood : recurrence) >= 7 ? colors.danger : (isReadOnly ? fr.recurrenceLikelihood : recurrence) >= 4 ? colors.statusYellow : colors.success }}>{isReadOnly ? fr.recurrenceLikelihood ?? '—' : recurrence}/10</strong></Label>
        {isReadOnly
          ? <div style={{ marginTop: '0.35rem', background: colors.surfaceMuted, borderRadius: 6, height: 8, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(fr.recurrenceLikelihood || 0) * 10}%`, background: colors.primary }} />
            </div>
          : <>
              <input type="range" min={0} max={10} step={1} value={recurrence} onChange={e => setRecurrence(Number(e.target.value))} style={{ width: '100%', marginTop: '0.35rem', accentColor: colors.primary }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: colors.textDim }}>
                <span>0 — Unlikely</span><span>10 — Very likely</span>
              </div>
            </>
        }
      </div>

      {complaint.status === 'monitoring' && (
        <div style={{ paddingTop: '0.5rem' }}>
          <button onClick={handleClose} disabled={saving} className="ni-button ni-button--primary">
            {saving ? 'Closing…' : 'Close Complaint & Generate Report'}
          </button>
        </div>
      )}

      {complaint.status === 'archived' && (
        <div style={{ background: `${colors.textDim}10`, borderRadius: 8, padding: '0.65rem 0.85rem', fontSize: '0.78rem', color: colors.textDim, textAlign: 'center' }}>
          This complaint is archived. All records are read-only.
        </div>
      )}
    </div>
  );
}

// ── Quality links ─────────────────────────────────────────────────────────────

const QUALITY_LINK_ALLOWED = ['draft', 'submitted', 'acknowledged', 'initial_report', '8d_report'];

function QualityTab({ complaint, apiCall, onUpdated }) {
  const [links, setLinks] = React.useState(complaint.qualityLinks || []);
  const [showSearch, setShowSearch] = React.useState(false);
  const [removing, setRemoving] = React.useState(null);
  const [expandedSnapshot, setExpandedSnapshot] = React.useState(null);
  const [expandedEvals, setExpandedEvals] = React.useState(null);
  const [evalNotes, setEvalNotes] = React.useState({});

  const canEdit = QUALITY_LINK_ALLOWED.includes(complaint.status);

  const handleEval = async (linkId, requirementId, result) => {
    setLinks(prev => prev.map(l => {
      if (String(l._id) !== String(linkId)) return l;
      return { ...l, evaluations: (l.evaluations || []).map(e => e.requirementId === requirementId ? { ...e, result } : e) };
    }));
    const d = await apiCall(`/complaints/${complaint._id}/quality-links/${linkId}/evaluations`, 'PATCH', { requirementId, result });
    if (d?.ok) { setLinks(d.qualityLinks); onUpdated({ ...complaint, qualityLinks: d.qualityLinks }); }
  };

  const handleEvalNote = async (linkId, requirementId) => {
    const note = evalNotes[`${linkId}:${requirementId}`];
    if (note === undefined) return;
    const d = await apiCall(`/complaints/${complaint._id}/quality-links/${linkId}/evaluations`, 'PATCH', { requirementId, note });
    if (d?.ok) { setLinks(d.qualityLinks); onUpdated({ ...complaint, qualityLinks: d.qualityLinks }); }
  };

  const handleRemove = async (linkId) => {
    if (!window.confirm('Remove this quality link?')) return;
    setRemoving(linkId);
    try {
      const d = await apiCall(`/complaints/${complaint._id}/quality-links/${linkId}`, 'DELETE');
      if (d?.ok) {
        setLinks(d.qualityLinks);
        onUpdated({ ...complaint, qualityLinks: d.qualityLinks });
      }
    } finally { setRemoving(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: colors.textStrong }}>Linked Quality Documents</div>
          <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: 2 }}>
            {canEdit
              ? 'Attach the quality procedures applicable to this NCR. Content is frozen at time of linking.'
              : 'Quality links are locked — the 8D report has been accepted.'}
          </div>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowSearch(true)}
            style={{ background: colors.primary, color: '#000', border: 'none', borderRadius: 6, padding: '7px 14px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', flexShrink: 0 }}
          >
            + Link Document
          </button>
        )}
      </div>

      {links.length === 0 ? (
        <div style={{ padding: '32px 0', textAlign: 'center', color: colors.textMuted, fontSize: '0.82rem' }}>
          No quality documents linked yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {links.map(l => (
            <div key={l._id} style={{ background: colors.surfaceMuted, border: `1px solid ${colors.panelBorder}`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: colors.primary, fontWeight: 700 }}>{l.documentNumber}</span>
                    <span style={{ fontSize: '0.72rem', background: `${colors.primary}18`, color: colors.primary, padding: '1px 7px', borderRadius: 4, fontWeight: 600 }}>Rev. {l.revision}</span>
                    {l.sectionRef && (
                      <span style={{ fontSize: '0.72rem', color: colors.textMuted, background: colors.surfaceRaised, padding: '1px 7px', borderRadius: 4 }}>{l.sectionRef}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: colors.textBody, marginTop: 3 }}>{l.documentTitle}</div>
                  {l.note && (
                    <div style={{ fontSize: '0.78rem', color: colors.textMuted, marginTop: 4, fontStyle: 'italic' }}>"{l.note}"</div>
                  )}
                  <div style={{ fontSize: '0.7rem', color: colors.textDim, marginTop: 5 }}>
                    Linked by {l.linkedByName || l.linkedBy} · {l.linkedAt ? new Date(l.linkedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginTop: 2 }}>
                  {l.snapshotContent && (
                    <button
                      onClick={() => setExpandedSnapshot(expandedSnapshot === l._id ? null : l._id)}
                      style={{ background: 'none', border: `1px solid ${colors.panelBorder}`, color: colors.textMuted, borderRadius: 5, padding: '3px 9px', fontSize: '0.72rem', cursor: 'pointer' }}
                    >
                      {expandedSnapshot === l._id ? 'Hide' : 'View snapshot'}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      onClick={() => handleRemove(l._id)}
                      disabled={removing === l._id}
                      style={{ background: 'none', border: 'none', color: colors.danger, fontSize: '0.72rem', cursor: 'pointer', padding: '3px 6px' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              {expandedSnapshot === l._id && l.snapshotContent && (
                <div style={{ marginTop: 12, padding: '12px 14px', background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 6, maxHeight: 320, overflowY: 'auto' }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textDim, marginBottom: 8 }}>
                    Frozen snapshot — {l.documentNumber} Rev. {l.revision}
                  </div>
                  <pre style={{ margin: 0, fontSize: '0.75rem', color: colors.textMuted, whiteSpace: 'pre-wrap', lineHeight: 1.6, fontFamily: 'monospace' }}>
                    {l.snapshotContent}
                  </pre>
                </div>
              )}

              {/* Evaluation summary + toggle */}
              {(l.evaluations || []).length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <EvalSummaryBadges evals={l.evaluations} />
                  <button
                    onClick={() => setExpandedEvals(expandedEvals === l._id ? null : l._id)}
                    style={{ background: 'none', border: `1px solid ${colors.panelBorder}`, color: colors.textMuted, borderRadius: 5, padding: '3px 9px', fontSize: '0.72rem', cursor: 'pointer' }}
                  >
                    {expandedEvals === l._id ? 'Hide evaluations' : 'Evaluate requirements'}
                  </button>
                </div>
              )}

              {/* Expanded evaluations panel */}
              {expandedEvals === l._id && (l.evaluations || []).length > 0 && (
                <div style={{ marginTop: 10, padding: '12px 14px', background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 6 }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textDim, marginBottom: 10 }}>
                    Requirements — {l.documentNumber} Rev. {l.revision}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {l.evaluations.map(ev => {
                      const noteKey = `${l._id}:${ev.requirementId}`;
                      const localNote = evalNotes[noteKey] !== undefined ? evalNotes[noteKey] : (ev.note || '');
                      return (
                        <div key={ev.requirementId} style={{ background: colors.surfaceMuted, borderRadius: 6, padding: '8px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                                <EvalSeverityBadge severity={ev.requirementSnapshot?.severity} />
                                <span style={{ fontSize: '0.7rem', color: colors.textDim, fontFamily: 'monospace' }}>{ev.requirementId}</span>
                                {ev.requirementSnapshot?.sectionNumber && (
                                  <span style={{ fontSize: '0.7rem', color: colors.textDim }}>§{ev.requirementSnapshot.sectionNumber}</span>
                                )}
                              </div>
                              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textBody }}>{ev.requirementSnapshot?.title}</div>
                              {ev.requirementSnapshot?.requirementText && (
                                <div style={{ fontSize: '0.73rem', color: colors.textMuted, marginTop: 2, lineHeight: 1.4 }}>{ev.requirementSnapshot.requirementText}</div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                              {[
                                { key: 'pass', label: '✓ Pass', color: colors.success },
                                { key: 'fail', label: '✗ Fail', color: colors.danger },
                                { key: 'not_applicable', label: 'N/A', color: colors.textMuted },
                              ].map(opt => (
                                <button
                                  key={opt.key}
                                  onClick={() => handleEval(String(l._id), ev.requirementId, opt.key)}
                                  style={{
                                    border: `1px solid ${ev.result === opt.key ? opt.color : colors.panelBorder}`,
                                    background: ev.result === opt.key ? opt.color + '20' : 'transparent',
                                    color: ev.result === opt.key ? opt.color : colors.textMuted,
                                    borderRadius: 5, padding: '2px 8px', fontSize: '0.72rem',
                                    cursor: 'pointer', fontWeight: ev.result === opt.key ? 700 : 400,
                                  }}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          {ev.result === 'fail' && (
                            <div style={{ marginTop: 6 }}>
                              <input
                                value={localNote}
                                onChange={e => setEvalNotes(n => ({ ...n, [noteKey]: e.target.value }))}
                                onBlur={() => handleEvalNote(String(l._id), ev.requirementId)}
                                placeholder="Describe how this requirement failed…"
                                style={{ width: '100%', background: colors.surface, border: `1px solid ${colors.panelBorder}`, color: colors.textBody, borderRadius: 4, padding: '5px 8px', fontSize: '0.73rem', boxSizing: 'border-box', outline: 'none' }}
                              />
                            </div>
                          )}
                          {ev.evaluatedByName && (
                            <div style={{ fontSize: '0.68rem', color: colors.textDim, marginTop: 4 }}>
                              {ev.evaluatedByName} · {ev.evaluatedAt ? new Date(ev.evaluatedAt).toLocaleDateString() : ''}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showSearch && (
        <QualityLinkSearchModal
          complaintId={complaint._id}
          apiCall={apiCall}
          onLinked={(newLinks) => { setLinks(newLinks); onUpdated({ ...complaint, qualityLinks: newLinks }); setShowSearch(false); }}
          onClose={() => setShowSearch(false)}
        />
      )}
    </div>
  );
}

function QualityLinkSearchModal({ complaintId, apiCall, onLinked, onClose }) {
  const [search, setSearch] = React.useState('');
  const [docs, setDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [sectionRef, setSectionRef] = React.useState('');
  const [note, setNote] = React.useState('');
  const [linking, setLinking] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const d = await apiCall(`/quality/documents?status=Released${search ? `&search=${encodeURIComponent(search)}` : ''}`, 'GET');
        if (d?.ok) setDocs(d.documents || []);
      } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  // Load on mount
  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await apiCall('/quality/documents?status=Released', 'GET');
        if (d?.ok) setDocs(d.documents || []);
      } finally { setLoading(false); }
    })();
  }, []); // eslint-disable-line

  const handleLink = async () => {
    if (!selected) return;
    setLinking(true); setError('');
    try {
      const d = await apiCall(`/complaints/${complaintId}/quality-links`, 'POST', {
        documentId: selected._id,
        sectionRef: sectionRef.trim(),
        note: note.trim(),
      });
      if (d?.ok) onLinked(d.qualityLinks);
      else if (d) setError(d.message || d.error || 'Failed to link');
    } catch (err) {
      setError(err?.message || 'Request failed');
    } finally { setLinking(false); }
  };

  const LEVEL_COLOR = { MASTER: '#94a3b8', FAMILY: '#60a5fa', SPECIFIC: '#46BB89' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 12, width: 560, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${colors.panelBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 800, fontSize: '0.92rem', color: colors.textStrong }}>Link Quality Document</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: '1rem' }}>✕</button>
        </div>

        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.panelBorder}` }}>
          <input
            autoFocus
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by document number or title…"
            style={{ width: '100%', background: colors.surfaceMuted, border: `1px solid ${colors.panelBorder}`, color: colors.textBody, borderRadius: 6, padding: '8px 12px', fontSize: '0.82rem', boxSizing: 'border-box', outline: 'none' }}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted, fontSize: '0.8rem' }}>Loading…</div>
          ) : docs.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted, fontSize: '0.8rem' }}>No released documents found.</div>
          ) : (
            docs.map(d => (
              <div
                key={d._id}
                onClick={() => setSelected(selected?._id === d._id ? null : d)}
                style={{
                  padding: '9px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                  background: selected?._id === d._id ? `${colors.primary}15` : 'transparent',
                  borderLeft: selected?._id === d._id ? `3px solid ${colors.primary}` : '3px solid transparent',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: colors.primary, fontWeight: 700 }}>{d.documentNumber}</span>
                    <span style={{ fontSize: '0.68rem', color: LEVEL_COLOR[d.level] || colors.textMuted, fontWeight: 600 }}>{d.level}</span>
                    <span style={{ fontSize: '0.68rem', color: colors.textDim }}>{d.documentType}</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: colors.textBody, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</div>
                  {d.productFamily && <div style={{ fontSize: '0.7rem', color: colors.textDim, marginTop: 1 }}>{d.productFamily}</div>}
                </div>
                <span style={{ fontSize: '0.7rem', color: colors.textMuted, fontFamily: 'monospace', flexShrink: 0 }}>Rev. {d.currentRevision}</span>
              </div>
            ))
          )}
        </div>

        {selected && (
          <div style={{ padding: '14px 20px', borderTop: `1px solid ${colors.panelBorder}`, background: colors.surfaceMuted }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: colors.primary, marginBottom: 10 }}>
              {selected.documentNumber} — {selected.title}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textMuted, marginBottom: 4 }}>Section / Requirement (optional)</label>
                <input
                  value={sectionRef}
                  onChange={e => setSectionRef(e.target.value)}
                  placeholder="e.g. Section 5 — Acceptance Criteria"
                  style={{ width: '100%', background: colors.surface, border: `1px solid ${colors.panelBorder}`, color: colors.textBody, borderRadius: 5, padding: '6px 10px', fontSize: '0.78rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textMuted, marginBottom: 4 }}>Note (optional)</label>
                <input
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="What requirement was not met?"
                  style={{ width: '100%', background: colors.surface, border: `1px solid ${colors.panelBorder}`, color: colors.textBody, borderRadius: 5, padding: '6px 10px', fontSize: '0.78rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            {error && <div style={{ color: colors.danger, fontSize: '0.78rem', marginBottom: 8 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleLink} disabled={linking} style={{ background: colors.primary, color: '#000', border: 'none', borderRadius: 6, padding: '7px 16px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                {linking ? 'Linking…' : 'Confirm Link'}
              </button>
              <button onClick={onClose} style={{ background: 'none', border: `1px solid ${colors.panelBorder}`, color: colors.textMuted, borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontSize: '0.8rem' }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── History ───────────────────────────────────────────────────────────────────

function HistoryTab({ complaint }) {
  const history = [...(complaint.statusHistory || [])].reverse();
  const stageTimes = getStageTimes(complaint);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {stageTimes.length > 0 && (
        <div style={{ background: colors.surfaceMuted, borderRadius: 8, padding: '0.85rem', marginBottom: '0.5rem' }}>
          <Label>Stage Timings</Label>
          <Grid style={{ marginTop: '0.5rem' }}>
            {stageTimes.map(t => <InfoItem key={t.label} label={t.label} value={t.value} />)}
          </Grid>
        </div>
      )}
      {history.length === 0 ? (
        <p style={{ color: colors.textDim, fontSize: '0.85rem' }}>No status history yet.</p>
      ) : (
        history.map((h, i) => (
          <div key={i} style={{ background: colors.surfaceMuted, borderRadius: 6, padding: '0.65rem 0.85rem', fontSize: '0.82rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
              <span style={{ fontWeight: 600, color: colors.textStrong }}>{STATUS_LABELS[h.status] || h.status}</span>
              <span style={{ color: colors.textDim }}>{h.date ? new Date(h.date).toLocaleDateString('en-CA') : '—'}</span>
            </div>
            {h.fromStatus && <div style={{ color: colors.textDim, fontSize: '0.75rem' }}>From: {STATUS_LABELS[h.fromStatus] || h.fromStatus}</div>}
            {h.by && <div style={{ color: colors.textMuted, fontSize: '0.78rem' }}>{h.by}</div>}
            {h.note && <div style={{ color: colors.textBody, marginTop: '0.25rem' }}>{h.note}</div>}
          </div>
        ))
      )}
    </div>
  );
}

// ── Shared layout helpers ─────────────────────────────────────────────────────

function StatusBanner({ type, icon, title, detail }) {
  const s = {
    success: { bg: `${colors.success}12`, border: `${colors.success}30`, title: colors.success },
    waiting: { bg: `${colors.primary}10`, border: `${colors.primary}25`, title: colors.primary },
    warn:    { bg: `${colors.statusYellow}10`, border: `${colors.statusYellow}35`, title: colors.statusYellow },
    action:  { bg: `${colors.danger}10`, border: `${colors.danger}30`, title: colors.danger },
  }[type] || { bg: colors.surfaceMuted, border: colors.panelBorder, title: colors.textMuted };
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: '0.75rem 1rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
      <span style={{ fontSize: '1rem', marginTop: '0.05rem', flexShrink: 0 }}>{icon}</span>
      <div>
        <div style={{ fontWeight: 700, fontSize: '0.82rem', color: s.title }}>{title}</div>
        {detail && <div style={{ fontSize: '0.78rem', color: colors.textMuted, marginTop: '0.2rem' }}>{detail}</div>}
      </div>
    </div>
  );
}

const actionBlock = {
  background: colors.surfaceMuted,
  border: `1px solid ${colors.panelBorder}`,
  borderRadius: 8,
  padding: '1rem',
};

const emptyState = {
  padding: '2.5rem 1.5rem',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function Label({ children, style }) {
  return <div style={{ fontSize: '0.7rem', color: colors.textDim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', ...style }}>{children}</div>;
}

function InfoItem({ label, value }) {
  return (
    <div style={{ flex: '0 1 auto' }}>
      <Label>{label}</Label>
      <div style={{ fontSize: '0.88rem', color: colors.textBody, marginTop: '0.2rem' }}>{value ?? '—'}</div>
    </div>
  );
}

function Grid({ children, style }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', ...style }}>{children}</div>;
}

function Divider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.25rem 0' }}>
      <div style={{ flex: 1, height: 1, background: colors.panelBorder }} />
      <span style={{ fontSize: '0.68rem', color: colors.textDim, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: colors.panelBorder }} />
    </div>
  );
}

// Cloudinary force-download variant of a delivery URL.
function fileDownloadUrl(url) {
  return url.includes('/upload/') ? url.replace('/upload/', '/upload/fl_attachment/') : url;
}

function FileList({ files, label, inline }) {
  if (!files || files.length === 0) return null;
  if (inline) {
    return (
      <span style={{ fontSize: '0.68rem', color: colors.textDim }}>
        {files.length} file{files.length > 1 ? 's' : ''}
      </span>
    );
  }
  const isImage = f => !!f.url && (f.mimeType || '').startsWith('image/');
  const images = files.filter(isImage);
  const others = files.filter(f => !isImage(f));
  return (
    <div>
      {label && <Label>{label}</Label>}
      {images.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.4rem' }}>
          {images.map(f => (
            <div key={f.id} style={{ width: 100 }}>
              <a href={f.url} target="_blank" rel="noreferrer" title={`${f.originalName} — open full size`}>
                <img src={f.url} alt={f.originalName} style={{ width: 100, height: 74, objectFit: 'cover', borderRadius: 7, border: `1px solid ${colors.panelBorder}`, display: 'block' }} />
              </a>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.15rem' }}>
                <span style={{ flex: 1, fontSize: '0.62rem', color: colors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.originalName}</span>
                <a href={fileDownloadUrl(f.url)} title="Download" style={{ fontSize: '0.7rem', color: colors.primary, textDecoration: 'none', flexShrink: 0 }}>⬇</a>
              </div>
            </div>
          ))}
        </div>
      )}
      {others.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.4rem' }}>
          {others.map(f => (
            <div key={f.id} style={{ fontSize: '0.78rem', color: colors.textMuted, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>📎</span>
              {f.url
                ? <a href={f.url} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>{f.originalName}</a>
                : <span>{f.originalName}</span>}
              {f.size ? <span style={{ color: colors.textDim }}>({(f.size / 1024).toFixed(0)} KB)</span> : null}
              {f.url
                ? <a href={fileDownloadUrl(f.url)} title="Download" style={{ fontSize: '0.75rem', color: colors.primary, textDecoration: 'none' }}>⬇</a>
                : <span style={{ fontSize: '0.68rem', color: colors.textDim, fontStyle: 'italic' }}>file no longer available</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EvalSeverityBadge({ severity }) {
  const COLORS = { Critical: '#EC2124', Major: '#f59e0b', Minor: '#60a5fa' };
  const color = COLORS[severity] || colors.textMuted;
  return (
    <span style={{ fontSize: '0.7rem', fontWeight: 700, color, background: color + '1A', padding: '1px 6px', borderRadius: 4 }}>
      {severity || '—'}
    </span>
  );
}

function EvalSummaryBadges({ evals }) {
  const pass = evals.filter(e => e.result === 'pass').length;
  const fail = evals.filter(e => e.result === 'fail').length;
  const na = evals.filter(e => e.result === 'not_applicable').length;
  const pending = evals.filter(e => e.result === 'pending').length;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {pass > 0 && <span style={{ fontSize: '0.7rem', color: colors.success, fontWeight: 600 }}>{pass} pass</span>}
      {fail > 0 && <span style={{ fontSize: '0.7rem', color: colors.danger, fontWeight: 700 }}>{fail} fail</span>}
      {na > 0 && <span style={{ fontSize: '0.7rem', color: colors.textMuted }}>{na} N/A</span>}
      {pending > 0 && <span style={{ fontSize: '0.7rem', color: colors.textDim }}>{pending} pending</span>}
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '1.5rem 1rem' };
const modal = { background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 14, width: '100%', maxWidth: 760, display: 'flex', flexDirection: 'column', boxShadow: colors.shadow, maxHeight: '95vh' };
const modalHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '1rem 1.25rem', borderBottom: `1px solid ${colors.panelBorder}`, gap: '1rem' };
